import React, {useEffect, useMemo, useState, lazy, Suspense} from 'react';
import TopBar from '../../Components/TopBar';
import Page from '../../Components/Containers/Page';
import { useParams } from 'react-router-dom';
import { statuses, useWorkDate, useWorkOrder } from '../../Data/Jobs';
import { PageLoading } from '../../Components/Loading';
import Calendar from '../../Components/Calendar';
import Icon from '../../Components/Icons/Icon';
import { getDateStr } from '../../Components/Formatting/DateTime';
import MarkAsDone from '../Customer/MarkAsDone';
import Skip from '../Customer/Skip';
import ScheduleJob from '../Customer/ScheduleJob';
import { useGrants } from '../../Data/Business';
import Dropdown from '../../Components/Dropdown';
import { usePreference } from '../../Data/Preference';
import { useSession } from '../../Components/Session';
import { Roles } from '../../Data/Users';
import Reordering from './Reordering';
import { useDialogues } from '../../Components/Dialogues';
import { useRedirect } from '../../Services/helpers';
import Preferences from './Preferences';

const Job = lazy(() => import(/* webpackPreload: true, webpackChunkName: "ui-components" */'./Job'));

const Work = () => {
    const {date} = useParams();
    const [assignedTo, setAssignedTo] = usePreference('work:assignedTo', null);
    const [hideCompleted] = usePreference('work:hideCompleted', false);
    const {jobs, refresh: refreshJobs} = useWorkDate(date, assignedTo, true);
    const {order, reorder, refresh: refreshOrder, isLoading: workOrderLoading, deleteWorkOrder} = useWorkOrder(date, assignedTo, true);
    const [markingJobDone, setMarkingJobDone] = useState(null);
    const [skipping, setSkipping] = useState(null);
    const [scheduling, setScheduling] = useState(null);
    const [expanded, setExpanded] = useState(null);
    const [calendarOpen, setCalendarOpen] = useState(false);
    const [isReordering, setIsReordering] = useState(false);
    const [preferencesOpen, setPreferencesOpen] = useState(false);
    const {confirm} = useDialogues();
    const {go, Redirect} = useRedirect();

    const dateStr = useMemo(() => getDateStr(date, false, true), [date]);

    const expandNext = () => {
        const open = expanded ?? markingJobDone?.id ?? skipping?.id ?? scheduling?.id ?? null;

        if (!jobs || !open) {
            return;
        }

        const next = findNextJob(jobs, open, order);

        setExpanded(next ?? null);
    }

    const refreshAndExpandNext = () => {
        expandNext();
        refreshJobs();
    }

    const refreshJobsAndOrder = async () => {
        await Promise.all([refreshJobs(), refreshOrder()]);
    }

    const goBack = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() - 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }
    const goForward = () => {
        const d = new Date(date+'T12:12:12Z');
        d.setDate(d.getDate() + 1);
        go('/work/'+d.toISOString().split('T')[0]);
    }

    useEffect(() => {
        refreshJobsAndOrder();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [assignedTo]);

    const dateOptions = useMemo(() => {
        return [-2, -1, 0, 1, 2, 3, 4].map(i => {
            const d = new Date(date+'T12:12:12Z');
            d.setDate(d.getDate() + i);
            return {
                icon: i < 0 ? 'UpChevron' : i > 0 ? 'DownChevron' : 'Circle',
                title: getDateStr(d.toISOString().split('T')[0], false, true),
                onClick: () => go('/work/'+d.toISOString().split('T')[0]),
            };
        });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [date]);

    return <>
        <TopBar
            left={<AssignedTo {...{assignedTo, setAssignedTo}} />}
            title={<div className="flex flex-row items-center justify-around gap-3">
                <Icon icon="LeftChevron" onClick={() => goBack()} className="cursor-pointer" />
                {dateStr}
                <Icon icon="RightChevron" onClick={() => goForward()} className="cursor-pointer" />
            </div>}
            dropdownOptions={[
                ...dateOptions,
                {
                    icon: 'CalendarDay',
                    title: 'Calendar',
                    onClick: () => setCalendarOpen(true),
                },
                {
                    div: true,
                },
                {
                    icon: 'ArrowsUpDown',
                    title: 'Reorder',
                    onClick: () => setIsReordering(true),
                    disabled: !jobs?.length ?? null,
                },
                {
                    icon: 'Slash',
                    title: 'Clear custom order',
                    onClick: () => confirm(
                        {
                            theme: 'warning',
                            title: 'Clear custom work order?',
                            confirmLabel: 'Clear order',
                            message: <>
                                Are you sure you want to clear the custom order for this worker/day?<br />
                                Jobs will be displayed in their default order from rounds or the calendar planner.
                            </>
                        },
                        async () => {
                            await deleteWorkOrder();
                            await refreshJobsAndOrder();
                        },
                    ),
                    hidden: !(order && order.jobIds && order.jobIds.length > 0),
                },
                {
                    div: true,
                },
                {
                    icon: 'Gear',
                    title: 'Preferences',
                    onClick: () => setPreferencesOpen(true),
                },
            ]}
        />

        <Page>
            <Inner {...{
                jobs,
                dateStr,
                setMarkingJobDone,
                setSkipping,
                setScheduling,
                expanded,
                setExpanded,
                isReordering,
                setIsReordering,
                order,
                reorder,
                workOrderLoading,
                onChange: refreshJobs,
                hideCompleted
            }} />
        </Page>

        <MarkAsDone job={markingJobDone}
            onClose={() => setMarkingJobDone(null)}
            then={refreshAndExpandNext}
        />
        <Skip job={skipping}
            onClose={() => setSkipping(null)}
            then={refreshAndExpandNext}
        />
        <ScheduleJob job={scheduling}
            onClose={() => setScheduling(null)}
            then={refreshAndExpandNext}
        />
        <Calendar onSelect={d => go('/work/'+d.date)}
            selected={date}
            isOpen={calendarOpen}
            setIsOpen={setCalendarOpen}
        />
        <Preferences isOpen={preferencesOpen} setIsOpen={setPreferencesOpen} />
        <Redirect />
    </>
}

const Inner = ({jobs, dateStr, setMarkingJobDone, setSkipping, setScheduling, expanded, setExpanded, isReordering, setIsReordering, order, reorder, workOrderLoading, onChange, hideCompleted}) => {
    if (!jobs || workOrderLoading) {
        return <PageLoading />
    }

    if (isReordering) {
        return <Reordering saveOrder={reorder} {...{jobs, setIsReordering, order}} />
    }

    return <Jobs {...{jobs, dateStr, setMarkingJobDone, setSkipping, setScheduling, expanded, setExpanded, isReordering, setIsReordering, order, onChange, hideCompleted}} />
}

const AssignedTo = ({assignedTo, setAssignedTo}) => {
    const {activeGrants} = useGrants();
    const {isRoleSufficient} = useSession();

    if (!activeGrants || !isRoleSufficient(Roles.Manager)) {
        return <Dropdown
            disabled
            icon="User"
        />;
    }

    const opt = (title, value) => ({
        value,
        title,
        onClick: () => setAssignedTo(value),
        icon: assignedTo === value ? 'CircleCheck' : 'Circle',
    });

    return <Dropdown
        openRight
        icon="User"
        iconTheme={assignedTo ? 'solid' : null}
        className={assignedTo ? 'fill-brand' : null}
        options={[
            {section: 'Assigned to'},
            opt('Anyone', null),
            ...activeGrants.map(grant => opt(grant.title, grant.userId)),
        ]}
    />
}

const Jobs = ({jobs, dateStr, setMarkingJobDone, setSkipping, setScheduling, expanded, setExpanded, order, onChange, hideCompleted}) => {

    if (jobs.length === 0)  {
        return <NoJobs dateStr={dateStr} />
    }
    if (order && order.jobIds && order.jobIds.length > 0) {
        const orderedJobs = order.jobIds.map(id => jobs.find(j => j.id === id)).filter(j => j);
        const unorderedJobs = jobs.filter(j => !order.jobIds.includes(j.id));
        jobs = [...orderedJobs, ...unorderedJobs];
    }

    const filtered = hideCompleted ? jobs.filter(j => !statuses[j.status].considerDone || j.id === expanded) : jobs;

    if (filtered.length === 0) {
        return <AllDone dateStr={dateStr} />
    }

    return <div className="flex flex-col divide-y divide-gray-200 overflow-visible">
        {filtered.map(job => {
            const isExpanded = expanded === job.id;
            const toggleIsExpanded = () => setExpanded((exp) => exp === job.id ? null : job.id);

            return <Suspense key={job.id} fallback={null}>
                <Job {...{isExpanded, toggleIsExpanded, job, setMarkingJobDone, setSkipping, setScheduling, onChange}} />
            </Suspense>
        })}
    </div>
}

const NoJobs = ({dateStr}) => {
    return <div className="text-center mt-5">
        <Icon icon="UmbrellaBeach" size="4x" center />
        <p className="font-bold mt-3">No jobs scheduled for {dateStr}</p>
        <p className="text-muted mt-3">To add jobs to your work view<br />use the Plan tab or Customer's page</p>
    </div>
}

const AllDone = ({dateStr}) => {
    return <div className="text-center mt-5">
        <Icon icon="PartyHorn" size="4x" className="fill-brand" center />
        <p className="font-bold mt-3">All done!</p>
        <p className="text-muted mt-3">
            You've completed all the jobs for {dateStr}.<br />
            Nice one.
        </p>
    </div>
}

const findNextJob = (jobs, jobId, order) => {
    let theJobs = jobs;

    if (order && order.jobIds && order.jobIds.length > 0) {
        const orderedJobs = order.jobIds.map(id => jobs.find(j => j.id === id)).filter(j => j);
        const unorderedJobs = jobs.filter(j => !order.jobIds.includes(j.id));
        theJobs = [...orderedJobs, ...unorderedJobs];
    }
    theJobs = theJobs.filter(j => !statuses[j.status].considerDone);

    const index = theJobs.findIndex(j => j.id === jobId);

    if (index === -1) {
        return null;
    }

    const next = theJobs[index + 1] ?? null;

    return next ? next.id : null;
}

export default Work;
